import { WIX_STORES } from '@wix/app-definition-ids';
import type { TpaPageLink } from '@wix/document-services-types';
import type { LinkWithPageId } from '@wix/document-services-types/src/link';
import type { ComponentRef, EditorSDK, PageRef } from '@wix/platform-editor-sdk';

import type { EditorReadyOptions } from '../../../../types';
import { log } from '../../../../utils/monitoring';
import { APP_TOKEN, BUILDER_MENU_ITEMS_TYPE, MENU_IDS, MenuItemIdentifier } from '../../../constants';
import { getIsEditor3 } from '../../../util';
import {
  createLoginIconsMenu,
  createLoginMenu,
  getMenuById,
  getMenuItems,
  updateMenuItems,
  updatePagePathInMenuItems,
} from '../../../wrappers/menus';
import { addLoginButton } from './login-bar';
import { getPageIdFromPageRef } from './pages';

export const addLoginMenus = async (editorSDK: EditorSDK, options: EditorReadyOptions) => {
  const isStoresInstallation = options.origin.info?.appDefinitionId === WIX_STORES;
  const headerRef = await editorSDK.siteSegments.getHeader(APP_TOKEN);

  if (!headerRef) {
    // site might have no header
    return;
  }

  await Promise.all([createLoginMenu(editorSDK), createLoginIconsMenu(editorSDK)]);

  try {
    const isMigrating = options.origin.info?.migrationData?.isMigrating;

    if (isMigrating) {
      return;
    }

    const widgetRef = await addLoginButton(editorSDK, headerRef, isStoresInstallation);

    if (getIsEditor3()) {
      await connectLoginMenusToInnerElements(editorSDK, widgetRef);
    }
  } catch (e) {
    log('Failed to add login button', { extra: { error: (e as Error).toString() } });
  }
};

export const addMemberSettingsSubMenu = async (editorSDK: EditorSDK) => {
  await editorSDK.menu.create(APP_TOKEN, {
    menuData: { name: 'Member Settings Menu', items: [], ...(getIsEditor3() && { type: BUILDER_MENU_ITEMS_TYPE }) },
    customId: MENU_IDS.SETTINGS_SUB_MENU_ID,
  });
};

export const connectLoginMenusToInnerElements = async (editorSDK: EditorSDK, menuCompRef: ComponentRef) => {
  await Promise.all([
    editorSDK.menu.connectToInnerElement(APP_TOKEN, {
      menuCompRef,
      menuId: MENU_IDS.LOGIN_MENU_ID,
      innerElementsPath: ['navbar'],
    }),

    editorSDK.menu.connectToInnerElement(APP_TOKEN, {
      menuCompRef,
      menuId: MENU_IDS.LOGIN_ICONS_MENU_ID,
      innerElementsPath: ['icons'],
    }),
  ]);
};

export const addProfileSubMenu = async (editorSDK: EditorSDK) => {
  await editorSDK.menu.create(APP_TOKEN, {
    menuData: { name: 'Member Menu', items: [], ...(getIsEditor3() && { type: BUILDER_MENU_ITEMS_TYPE }) },
    customId: MENU_IDS.SUB_MENU_ID,
  });
};

export const updateSettingsPagePathInMenus = async (
  editorSDK: EditorSDK,
  currentPageUriSEO: string,
  newPageUriSEO: string,
) => {
  return Promise.all([
    updatePagePathInMenuItems({
      editorSDK,
      menuId: MENU_IDS.LOGIN_MENU_ID,
      currentPageUriSEO,
      newPageUriSEO,
    }),
    updatePagePathInMenuItems({ editorSDK, menuId: MENU_IDS.LOGIN_ICONS_MENU_ID, currentPageUriSEO, newPageUriSEO }),
    updatePagePathInMenuItems({ editorSDK, menuId: MENU_IDS.SETTINGS_SUB_MENU_ID, currentPageUriSEO, newPageUriSEO }),
  ]);
};

export const updateProfilePagePathInMenus = async (
  editorSDK: EditorSDK,
  currentPageUriSEO: string,
  newPageUriSEO: string,
) => {
  return Promise.all([
    updatePagePathInMenuItems({
      editorSDK,
      menuId: MENU_IDS.LOGIN_MENU_ID,
      currentPageUriSEO,
      newPageUriSEO,
    }),
    updatePagePathInMenuItems({ editorSDK, menuId: MENU_IDS.SUB_MENU_ID, currentPageUriSEO, newPageUriSEO }),
  ]);
};

const removeProfilePageMenuItemsFromLoginBar = async (editorSDK: EditorSDK) => {
  const currentItems = await getMenuItems({ editorSDK, menuId: MENU_IDS.LOGIN_MENU_ID });

  const updatedMenuItems = currentItems.filter((menuItem) => {
    if (menuItem.link?.type === 'TpaPageLink') {
      const link = menuItem.link as TpaPageLink;
      return link.itemTypeIdentifier !== MenuItemIdentifier.SubPage;
    }

    return true;
  });

  return updateMenuItems({ editorSDK, menuId: MENU_IDS.LOGIN_MENU_ID, items: updatedMenuItems });
};

export const removeProfilePageMenuItemsFromMenus = async (editorSDK: EditorSDK) => {
  return Promise.all([
    updateMenuItems({ editorSDK, menuId: MENU_IDS.SUB_MENU_ID, items: [] }),
    removeProfilePageMenuItemsFromLoginBar(editorSDK),
  ]);
};

export const updateLoginBarMenuItemsLabels = async (editorSDK: EditorSDK, pageRef: PageRef, newTitle: string) => {
  const loginMenu = await getMenuById({ editorSDK, menuId: MENU_IDS.LOGIN_MENU_ID });

  const updatedLoginMenuItems = loginMenu.items.map((item) => {
    const link = item.link as LinkWithPageId;
    const pageId = getPageIdFromPageRef(pageRef);

    return link.pageId === `#${pageId}` ? { ...item, label: newTitle } : item;
  });

  await updateMenuItems({ editorSDK, menuId: MENU_IDS.LOGIN_MENU_ID, items: updatedLoginMenuItems });
};
